import { api } from '@/lib/api';
import type {
  ControlledDocumentDetail,
  ControlledDocumentsFilters,
  ControlledDocumentsResponse,
  EqmsSummary,
  EqmsTransitionInput,
  EqmsUpdateRequest,
  EqmsUpdateRequestDecision,
  EqmsUpdateRequestList,
  EqmsUpdateRequestQuery,
  EqmsUpdateRequestStatus,
} from '../types/eqms.types';

const BASE = '/v4/dms/controlled-documents';

export function getEqmsSummary(): Promise<EqmsSummary> {
  return api.get<EqmsSummary>(`${BASE}/summary`);
}

export function getControlledDocuments(
  filters: ControlledDocumentsFilters,
): Promise<ControlledDocumentsResponse> {
  return api.get<ControlledDocumentsResponse>(BASE, filters as unknown as Record<string, unknown>);
}

export function getControlledDocument(id: string): Promise<ControlledDocumentDetail> {
  return api.get<ControlledDocumentDetail>(`${BASE}/${id}`);
}

export function getControlledDocumentPreview(
  id: string,
  versionId?: string,
  assignmentId?: string,
): Promise<{ previewUrl: string; mimeType: string; expiresAt: string }> {
  return api.get(`${BASE}/${id}/preview-url`, versionId || assignmentId ? { versionId, assignmentId } : undefined);
}

export function transitionControlledDocument(id: string, input: EqmsTransitionInput) {
  return api.post<{
    document: ControlledDocumentDetail['document'];
    signature: ControlledDocumentDetail['signatures'][number];
  }>(`${BASE}/${id}/transition`, input, { skipAuthRedirect: true });
}

const UPDATE_REQUESTS = '/v4/dms/eqms-update-requests';

export function getUpdateRequests(query: EqmsUpdateRequestQuery): Promise<EqmsUpdateRequestList> {
  return api.get<EqmsUpdateRequestList>(UPDATE_REQUESTS, {
    scope: query.scope,
    documentId: query.documentId,
    status: query.status?.join(','),
  });
}

/** `requestKey` stays the same across retries of one submit so it is never filed twice. */
export function createUpdateRequest(input: { documentId: string; reason: string; requestKey: string }) {
  return api.post<{ request: EqmsUpdateRequest; replayed: boolean }>(UPDATE_REQUESTS, input);
}

export function decideUpdateRequest(id: string, input: {
  status: EqmsUpdateRequestDecision;
  expectedStatus: EqmsUpdateRequestStatus;
  note?: string;
}) {
  return api.post<{ request: EqmsUpdateRequest }>(`${UPDATE_REQUESTS}/${id}/decision`, input);
}
